import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MuiPaper, MuiButton, MuiTab } from '@ace/ace-ui-web';
// Components
import KpiGridSection from '../kpigridsection';
import MuiDataGrid from '../muidatagrid';
import { createKpiColumns } from '../muidatagrid/columnDefs/kpiColumns';
import SkeletonDataGrid from '../skeletons/common/SkeletonDataGrid';
// Redux
import { useAppSelector, useAppDispatch } from '../../redux';
import {
  selectKpiGridsByCardName,
  selectGridLoading,
} from '../../redux/OperationsReducer/operationsSlice';
import { selectUserDetails, selectIsMultiStoreEnabled } from '../../redux/UserReducer/userSlice';
// Languages
import lang from '../../languages/en.json';
// Utils
import { isMobileDevice, formatLoadDate } from '../../utils/appUtils';
import { handleExternalNavigation } from '../../utils/permissionUtils';
import { useClickHandler, type ClickHandler } from '../../utils/usageMetricsLogger';
// Constants
import { CARD_TITLES, PERMISSIONS } from '../../constants/constants';
// Models
import type { KpiGrid, KpiDetail } from '../../models/operationsmodels/KpiModels';
// Styles
import styles from './MuiKpiPaper.module.css';
import pagesStandardStyles from '../../pages/Pages.module.css';
// Hooks
import { useBuildDynamicUrl } from '../../hooks/useDynamicUrl';
import { useScrollSpy } from '../../hooks/useScrollSpy';
// Components
import MarketingDisclaimerDialog from '../marketingdisclaimerdialog';

const RESPONSIVE_BREAKPOINT_QUERY = '(max-width:1195px)';
const MOBILE_BREAKPOINT_QUERY = '(max-width:600px)';
/**
 * Props interface for MuiKpiPaper component
 */
interface MuiKpiPaperProps {
  /** Card title to determine which content to display */
  cardTitle: 'Marketing' | 'People' | 'B2B' | 'Ecommerce' | 'Inventory';
  /** Pre-fetched data for print view — skips Redux read when provided */
  data?: KpiGrid[];
  /** When true, renders all grids flat without tabs or interactive controls */
  isPrintView?: boolean;
}
const PAGE_NM = 'Operations';
/**
 * MuiKpiPaper Component - Reusable KPI display with scroll-stack navigation
 * Features:
 * - All KPI sections rendered stacked vertically
 * - Label-only tab strip that smooth-scrolls to the selected section
 * - Scroll spy keeps the active tab in sync with scroll position
 * - Conditional action buttons per section (Marketing Planning Tool, OSQ Settings)
 * - Peer group data toggle (per section)
 * - Responsive: tabs become scrollable below 1195px, font shrinks below 600px
 */
const MuiKpiPaper: React.FC<MuiKpiPaperProps> = ({
  cardTitle,
  data: dataProp,
  isPrintView = false,
}) => {
  // Redux state
  const dispatch = useAppDispatch();
  const buildDynamicUrl = useBuildDynamicUrl();
  const reduxKpiGrids = useAppSelector(state =>
    selectKpiGridsByCardName(state, cardTitle.toUpperCase()),
  );
  const isGridLoading = useAppSelector(selectGridLoading);
  const userDetails = useAppSelector(selectUserDetails);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  // Use prop data (print view) or Redux data (live view)
  const kpiGrids = dataProp ?? reduxKpiGrids;
  // Component state
  const [showPeerGroup, setShowPeerGroup] = React.useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = React.useState(0);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = React.useState(false);

  // Media queries for responsive tab strip
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const isResponsive = useMediaQuery(RESPONSIVE_BREAKPOINT_QUERY);

  // Section refs power smooth-scroll on tab click and scroll-spy tab highlight
  const sectionRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  useScrollSpy(sectionRefs, setActiveTab, kpiGrids?.length ?? 0);

  // Usage metrics click handler
  const handleAndLogClick = useClickHandler(PAGE_NM, cardTitle);
  const prefixedLogClick: ClickHandler = React.useCallback(
    (clickId, options) => handleAndLogClick(clickId, options),
    [handleAndLogClick],
  );
  const handleTabChange = React.useCallback((index: number): void => {
    setActiveTab(index);
    sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const openDisclaimer = React.useCallback((): void => {
    setIsDisclaimerOpen(true);
  }, []);
  const handleDisclaimerClose = React.useCallback((): void => {
    setIsDisclaimerOpen(false);
  }, []);
  const handleDisclaimerAccept = React.useCallback((): void => {
    handleExternalNavigation(
      userDetails?.userPermissionList,
      PERMISSIONS.MARKETING_PLANNING_TOOL,
      'dynamicURL:ACENET',
      dispatch,
      buildDynamicUrl,
      'MPT_BTN',
    );
    handleDisclaimerClose();
  }, [userDetails, dispatch, buildDynamicUrl, handleDisclaimerClose]);
  // Build the conditional action button slot for a given section.
  // Marketing → Marketing Planning Tool (opens disclaimer dialog).
  // Inventory → OSQ Settings (external nav to ACENET).
  const buildSectionActionButtons = (section: KpiGrid): React.ReactNode | undefined => {
    if (
      cardTitle === CARD_TITLES.MARKETING &&
      section.title === lang.marketing.sections.localMarketing &&
      !isMobileDevice()
    ) {
      return (
        <MuiButton
          variant="outlined"
          iconType="volumeUp"
          iconPosition="left"
          onClick={() =>
            prefixedLogClick(
              `${section.title} - ${lang.marketing.buttons.marketingPlanningTool}`,
              {
                callback: openDisclaimer,
              },
            )
          }>
          {lang.marketing.buttons.marketingPlanningTool}
        </MuiButton>
      );
    }
    if (
      cardTitle === CARD_TITLES.INVENTORY &&
      section.title === lang.inventory.sections.optimalShelfQuantity &&
      !isMobileDevice()
    ) {
      return (
        <MuiButton
          colors="secondary"
          iconType="launch"
          iconPosition="right"
          onClick={() =>
            prefixedLogClick('Optimal Shelf Quantity Settings', {
              callback: () =>
                handleExternalNavigation(
                  userDetails?.userPermissionList,
                  PERMISSIONS.CONFIDENTIAL_CONTENT_READ_ONLY,
                  'dynamicURL:ACENET',
                  dispatch,
                  buildDynamicUrl,
                  'OSQA_BTN',
                ),
            })
          }>
          {lang.inventory.buttons.osqSettings}
        </MuiButton>
      );
    }
    return undefined;
  };

  // Memoized onChange handler passed to MuiTab child component
  const handleTabClick = React.useCallback(
    (index: number): void => {
      const tabTitle = kpiGrids[index]?.title?.toUpperCase() ?? String(index);
      prefixedLogClick(`Section Tab - ${tabTitle}`, {
        callback: () => handleTabChange(index),
      });
    },
    [kpiGrids, prefixedLogClick, handleTabChange],
  );
  // Show skeleton only while loading
  if (isGridLoading) {
    // In print view with empty data, render nothing (section doesn't exist for this store)
    if (isPrintView) return null;
    return <SkeletonDataGrid />;
  }

  // Show no-data message if API succeeded but returned no grids for this card
  if (!kpiGrids || kpiGrids.length === 0) {
    if (isPrintView) return null;
    return (
      <div className={pagesStandardStyles.errorContent}>
        <p>{lang.pageContent.noData}</p>
      </div>
    );
  }

  // Print view: render all grids flat with data-pdf-grid markers (no tabs, no interactive controls)
  if (isPrintView) {
    return (
      <>
        {kpiGrids.length > 0 && <h1 className={styles.topSectionTitle}>{cardTitle}</h1>}
        {kpiGrids.map((section, index) => (
          <div
            key={`${cardTitle}-${section.title}-${index}`}
            className={styles.printGridBlock}
            data-pdf-grid>
            <div className={pagesStandardStyles.headerContainer}>
              <div>
                <h2 className={pagesStandardStyles.pageTitle}>{section.title}</h2>
                <p className={pagesStandardStyles.pageSubtitle}>
                  {lang.pageContent.datePrefix}{' '}
                  {section.loadDate ? formatLoadDate(section.loadDate, false, -1) : 'N/A'}
                </p>
              </div>
            </div>
            <MuiDataGrid<KpiDetail, [boolean, boolean, string, string]>
              rows={section.kpiDetails}
              getRowId={(row: KpiDetail) => row.kpiCode}
              createColumns={createKpiColumns}
              columnArgs={[true, isMultiStoreEnabled, section.title, section.cardName]}
              isPrintView={true}
              gridKey={`print-${cardTitle}-${section.title}-${index}`}
              autoFitValueColumns={section.cardName === CARD_TITLES.B2B}
            />
            {/* Print-only benchmark note (multi-store only) for B2B grids and the Assortment and Pricing section */}
            {isMultiStoreEnabled &&
              (cardTitle === 'B2B' || section.title === 'Assortment and Pricing') && (
                <div className={pagesStandardStyles.printBenchmarkNote}>
                  <InfoOutlinedIcon
                    fontSize="small"
                    className={pagesStandardStyles.printBenchmarkNoteIcon}
                  />
                  <span>{lang.pageContent.benchmarkPerStoreAveragesNote}</span>
                </div>
              )}
          </div>
        ))}
      </>
    );
  }
  return (
    <MuiPaper
      elevation={0}
      square
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        margin: '0 auto 15px',
      }}>
      <div>
        {/* Label-only tab strip — drives smooth scroll to the matching section below.
           Hidden when there's only one section (a single-tab strip adds no value). */}
        {kpiGrids.length > 1 && (
          <nav>
            <MuiTab
              fontSize={isMobile ? 'small' : 'default'}
              scrollable={isResponsive}
              renderPanels={false}
              value={activeTab}
              onChange={handleTabClick}
              tabs={kpiGrids.map(section => ({
                content: null,
                label: section.title.toUpperCase(),
              }))}
            />
          </nav>
        )}

        {/* All sections rendered stacked; scroll-spy keeps the tab strip in sync */}
        {kpiGrids.map((section, index) => (
          <div
            key={section.title || index}
            ref={el => (sectionRefs.current[index] = el)}
            className={styles.gridSection}>
            <KpiGridSection
              section={section}
              isPeerGroupVisible={showPeerGroup[section.title] ?? true}
              onTogglePeerGroup={checked =>
                setShowPeerGroup(prev => ({
                  ...prev,
                  [section.title]: checked,
                }))
              }
              actionButtons={buildSectionActionButtons(section)}
              logClick={(clickId, opts) => prefixedLogClick(`${section.title} - ${clickId}`, opts)}
              onDisclaimerOpen={openDisclaimer}
            />
          </div>
        ))}
      </div>
      <MarketingDisclaimerDialog
        isOpen={isDisclaimerOpen}
        onClose={handleDisclaimerClose}
        onAccept={handleDisclaimerAccept}
      />
    </MuiPaper>
  );
};
export default MuiKpiPaper;
